import Drawer from '@material-ui/core/Drawer';
import { CreationTaskDrawer } from 'features/daily-plan';
import { TASK_TYPES } from 'features/daily-plan/constants';
import {
  CompanyPaymentStatusAlert,
  ProfileAccountableBar,
  ProfileMenu,
  ProfilePageLayout,
  ProfileSummary,
  ProfileTabs
} from 'features/inventory-profiles/components/shared';
import {
  COMMON_TAB_IDS,
  COMPANY_DRAWERS,
  COMPANY_PROFILE_CONFIG,
  COMPANY_TAB_IDS
} from 'features/inventory-profiles/constants';
import {
  useCompanyProfile,
  useProfileAccountability,
  useProfileDrawer,
  useTabRefresh
} from 'features/inventory-profiles/hooks';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import { When } from 'UI/components/atoms';
import CompanyEdit from 'UI/components/organisms/CompanyEdit';
import CompanyForm from 'UI/components/organisms/CompanyForm';
import CompanySignStatusForm from 'UI/components/organisms/CompanySignStatusForm';
import {
  ClaimAccountabilityDrawer,
  useClaimAccountability
} from 'UI/components/organisms/inventoryProfiles/ClaimAccountability';
import { handleEntityEditionCompleted } from 'UI/components/organisms/inventoryProfiles/utils';
import ReassignRecruiterForm from 'UI/components/organisms/ReassignRecruiter';
import SendoutManager from 'UI/components/organisms/sendouts';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyEntity, EntityType, SearchProjectEntities } from 'UI/constants/entityTypes';

import {
  useCompanyNavigation,
  useCompanyPermissions,
  useSignStatusFromUrl
} from './CompanyProfileV2.hooks';
import { buildCompanyTabs } from './CompanyProfileV2.tabs';
import {
  buildCompanyMenuItems,
  formatCompanyResponse,
  getCompanyTypeDialog
} from './CompanyProfileV2.utils';

/**
 * Company profile. Composes the data, permissions and actions hooks with the shared layout.
 * @param {Object} props
 * @param {number|string} props.id Company id, from the route or the quick view
 * @param {boolean} [props.enableUrlNavigation] Sync the active tab with the URL. True by default
 *   (route); the quick view passes false
 * @param {Function} [props.onEditionCompleted] Called after a profile edit in quick-view mode
 * @param {string} [props.layoutClassName] Extra className for the content area (quick view)
 */
export const CompanyProfileV2 = ({
  enableUrlNavigation = true,
  id,
  layoutClassName,
  onEditionCompleted
}) => {
  const { company, recruiterRoles, isAlreadyAccountable, isLoading, error, refresh, reload } =
    useCompanyProfile(id);
  const isProfileReady = !isLoading && !error && Boolean(company.id);

  const claim = useClaimAccountability({
    entity: COMPANY_PROFILE_CONFIG.id,
    isAlreadyAccountable
  });
  const formattedCompany = formatCompanyResponse(company);

  const permissions = useCompanyPermissions(company, recruiterRoles);

  const { refreshTab, getTabVersion } = useTabRefresh();
  const {
    drawer,
    drawerItemId,
    openDrawer,
    openItemDrawer,
    closeDrawer,
    closeAndRefresh,
    closeAndRefreshTab
  } = useProfileDrawer({ onChanged: refresh, onTabChanged: refreshTab });
  const assignment = useProfileAccountability({
    canEdit: permissions.canEdit,
    entity: COMPANY_PROFILE_CONFIG,
    profile: company,
    onReassign: openDrawer(COMPANY_DRAWERS.Recruiters)
  });

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      endpoint: `${COMPANY_PROFILE_CONFIG.endpoint}/${id}`,
      entity: SearchProjectEntities.Company,
      profile: { id: company.id, name: formattedCompany.header.title },
      tabKey: COMMON_TAB_IDS.SearchProjects,
      triggerTabRefresh: refreshTab,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  const tabs = buildCompanyTabs({
    company,
    companyName: formattedCompany.header.title,
    isLoading,
    permissions,
    openItemDrawer,
    refresh,
    refreshTab,
    getTabVersion,
    searchProjectsTab: searchProjectTabPropsDefinition
  });

  const signStatus = useSignStatusFromUrl();
  const companyTypeDialog = getCompanyTypeDialog({
    companyTypeReassure: company.companyTypeReassure,
    drawer,
    closeDrawer
  });
  const navigation = useCompanyNavigation();
  const refreshAfterClaim = () => {
    claim.handleSuccessClaimAccountability();
    refresh();
  };
  const menuItems = buildCompanyMenuItems({
    permissions,
    canManage: assignment.canManage,
    canView: assignment.canView,
    searchProjectMenuItems: SearchProjectsMenuItems,
    claimMenuItem: claim.claimAccountabilityMenuItem,
    openDrawer
  });

  return (
    <>
      <ProfilePageLayout
        header={formattedCompany.header}
        actions={<ProfileMenu items={menuItems} />}
        subheader={
          <>
            <ProfileSummary
              company={formattedCompany.summary.company}
              location={formattedCompany.summary.location}
              status={formattedCompany.status}
              created={formattedCompany.summary.created}
              details={formattedCompany.summary.details}
            />
            <ProfileAccountableBar assignment={assignment} />
            <CompanyPaymentStatusAlert companyId={id} isLoading={isLoading} />
          </>
        }
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        enableUrlNavigation={enableUrlNavigation}
        layoutClassName={layoutClassName}
      >
        <ProfileTabs
          tabs={tabs}
          enableUrlNavigation={enableUrlNavigation}
          organizationStorageKey={COMPANY_PROFILE_CONFIG.tabsStorageKey}
          organizationSubheader={COMPANY_PROFILE_CONFIG.tabsSubheader}
        />
      </ProfilePageLayout>

      <When condition={isProfileReady && companyTypeDialog.isOpen}>
        <CompanySignStatusForm
          type="dialog"
          loading={false}
          companyId={id}
          company={company}
          dialogProps={companyTypeDialog.dialogProps}
          reassure={companyTypeDialog.reassure}
          onUpdateCompany={refresh}
        />
      </When>
      <When condition={isProfileReady && signStatus.isOpen}>
        <CompanySignStatusForm
          type="drawer"
          loading={false}
          company={company}
          companyId={id}
          onUpdateCompany={refresh}
          followUpId={signStatus.followUpId}
          onCloseDrawer={signStatus.dismiss}
        />
      </When>
      <When condition={drawer === COMPANY_DRAWERS.Sendout}>
        <Drawer open onClose={closeDrawer}>
          <SendoutManager
            id={drawerItemId}
            onEdit={closeAndRefreshTab(COMPANY_TAB_IDS.Sendouts)}
            onDelete={closeAndRefreshTab(COMPANY_TAB_IDS.Sendouts)}
            onClose={closeDrawer}
            onNavigate={navigation.navigateFromSendout}
          />
        </Drawer>
      </When>
      <When condition={drawer === COMPANY_DRAWERS.Edit}>
        <CompanyEdit
          company={company}
          onEditCompleted={handleEntityEditionCompleted({
            handleEditCompleted: closeAndRefresh,
            enableUrlNavigation,
            onEditionCompleted
          })}
          onEditClosed={closeDrawer}
          CompanyForm={<CompanyForm disabledEditKeys={permissions.disabledEditKeys} />}
        />
      </When>
      <When condition={drawer === COMPANY_DRAWERS.Recruiters}>
        <ReassignRecruiterForm
          baseEndpoint={Endpoints.Companies}
          item={company}
          entityType={CompanyEntity}
          onReassignCompleted={closeAndRefresh}
          onReassignClosed={closeDrawer}
        />
      </When>
      <When condition={drawer === COMPANY_DRAWERS.Task}>
        <CreationTaskDrawer
          allowedTaskTypes={[TASK_TYPES.marketCandidate, TASK_TYPES.other]}
          onClose={closeDrawer}
          entityData={{ id: company.id, name: company?.name }}
          entityType={EntityType.Company}
          onTaskCreated={navigation.openDailyPlan}
        />
      </When>
      <When condition={claim.openClaimAccountabilityDrawer}>
        <ClaimAccountabilityDrawer
          entityId={id}
          entityType={COMPANY_PROFILE_CONFIG.id}
          open={claim.openClaimAccountabilityDrawer}
          onClose={claim.handleCloseClaimAccountabilityDrawer}
          onSuccess={refreshAfterClaim}
        />
      </When>
      <SearchProjectForms />
    </>
  );
};
